/**
 * FloatingChat v2 — Sprint 24 (floating-chat rebuild)
 *
 * Full replacement of Sprint 21 FloatingChat. Clean-slate rebuild per
 * seq #11176 spec + Claude Design handoff. Key changes:
 * - Two tabs: Chat (team feed + favorites filtering) + Help (ask @relay)
 * - ERComposerV1 handles ALL Chat posting (no custom Firestore logic)
 * - Bubble component with tails, pill shape, author dots
 * - Favorites picker + filter mode
 * - Misregistration ghosts (orange + yellow offset borders)
 * - Josefin Sans typography (font-system-handoff); wordmark uses Special Elite
 * - Resize handles on all 8 edges/corners
 */

import { useEscapeToClose } from "../hooks/useEscapeToClose";
import { centerWindow, clampWindow, isTitleBarTarget } from "../lib/windowBounds";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit as fbLimit,
  onSnapshot,
} from "firebase/firestore";
import type { User } from "firebase/auth";
import { db } from "../lib/firebase";
import { postWithSequence } from "../lib/teamLog";
import type { Mentionable } from "../hooks/useMentionables";
import { useManagementTeamFeed, type ManagementTeamMessage } from "../hooks/useManagementTeamFeed";
import { ERComposerV1 } from "./ERComposerV1";
import { ResizeHandle } from "./ResizeHandle";
import { HowRelayWorksText } from "./HowRelayWorksText";
import type { TeamLogMessage } from "../types";
import type { ProjectRecord } from "../lib/projectsMigration";

/* ========== Design tokens (from Claude Design) ========== */
const MC = {
  teal: "#5FC4C0",
  tealLight: "#7BCBBA",
  tealDark: "#054750",
  yellow: "#F5D347",
  orange: "#F4A460",
  paper: "#FFFFFF",
  ink: "#111111",
  inkSoft: "#3D3D3D",
  inkMuted: "#6B6B65",
  line: "#E6E2D4",
};

/* Author dot colors */
const AUTHOR_DOT: Record<string, string> = {
  "claude-code": MC.tealDark,
  "advisor": MC.yellow,
  "claude-research": MC.teal,
  "relay": MC.tealLight,
  "travis": MC.orange,
  "travisbernritter@gmail.com": MC.orange,
};

/* Roster for favorites picker — built from the workspace's mentionables
   (track:mention-roster, #51157), never hard-coded. */
interface RosterEntry {
  name: string;
  kind: "ai" | "human";
  role: string;
}

export function toRoster(mentionables: Mentionable[]): RosterEntry[] {
  return mentionables.map((m) => ({
    name: m.name,
    kind: m.kind === "human" ? "human" : "ai",
    role: m.label || (m.kind === "relay" ? "Assistant" : m.kind === "advisor" ? "Advisor" : "Teammate"),
  }));
}

/* Firestore collections */
const COLLECTION = "team_log_messages";

/* Panel sizing */
const DEFAULT_SIZE = { w: 400, h: 580 };
const MIN_SIZE = { w: 340, h: 420 };
const MAX_SIZE = { w: 620, h: 900 };

export interface FloatingChatHandle {
  openToTab: (tab: "chat" | "help") => void; // "help" activates the Help filter
}

interface Props {
  viewerAgentId: string;
  workspaceId: string;
  user: User;
  participants?: string[];
  /** Who can be @mentioned (relay, humans, advisors) — feeds the favorites picker. */
  mentionables?: Mentionable[];
  onOptimisticInsert?: (msg: TeamLogMessage) => void;
  onOptimisticRollback?: (id: string) => void;
  /** track:track-backlog--cc-composer-project-tag-validator — passed through to
   * ERComposerV1 for the missing-project-tag warning. Same `relay_workspaces`
   * doc App.tsx already subscribes to; no extra listener here. */
  projects?: ProjectRecord[];
  activeProjectSlug?: string | null;
  /** seq #22348 — passed through to ERComposerV1/TagPicker to gate the
   * "directive" tag client-side. */
  isHumanLead?: boolean;
}

export const FloatingChat = forwardRef<FloatingChatHandle, Props>(function FloatingChat({ viewerAgentId, workspaceId, user, participants, mentionables = [], onOptimisticInsert, onOptimisticRollback, projects, activeProjectSlug, isHumanLead = false }, ref) {
  const roster = useMemo(() => toRoster(mentionables), [mentionables]);
  // State
  const [open, setOpen] = useState(false);
  const [helpFilterActive, setHelpFilterActive] = useState<boolean>(() => {
    try {
      return localStorage.getItem("relay.fc.helpFilter") === "true";
    } catch {
      return false;
    }
  });
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.fc.pos") || "null");
      if (v && typeof v.x === "number") return v;
    } catch {}
    return null;
  });
  const [size, setSize] = useState(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.fc.size") || "null");
      if (v && typeof v.w === "number") return v;
    } catch {}
    return DEFAULT_SIZE;
  });
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.fc.favorites") || "null");
      if (Array.isArray(v)) return v;
    } catch {}
    return ["relay"]; // Seed default — no hard-coded teammates
  });
  const [activeFilters, setActiveFilters] = useState<string[]>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("relay.fc.activeFilters") || "null");
      if (Array.isArray(v)) return v;
    } catch {}
    return [];
  });
  const [tabY, setTabY] = useState<number | null>(() => {
    try {
      const v = parseFloat(localStorage.getItem("relay.fc.tabY") || "");
      if (!isNaN(v)) return v;
    } catch {}
    return null;
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  // track:track-backlog--perf-defer-floatingchat-listeners-to-open — initial page size for the
  // team feed; grows via "Load earlier messages", resets to 10 whenever the panel closes.
  const [teamFeedPageSize, setTeamFeedPageSize] = useState(10);
  // Refs
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const tabRef = useRef<HTMLButtonElement>(null);
  const isLoadingMoreRef = useRef(false);

  useImperativeHandle(ref, () => ({
    openToTab(tab) {
      setOpen(true);
      setHelpFilterActive(tab === "help");
    },
  }));

  useEffect(() => {
    if (!open) setTeamFeedPageSize(10);
  }, [open]);

  // Hooks — both listeners below are gated on `open`: zero Firestore reads until the
  // panel is actually opened (track:track-backlog--perf-defer-floatingchat-listeners-to-open).
  const { messages: teamFeed, loading, hasMore: teamFeedHasMore } = useManagementTeamFeed(workspaceId, {
    enabled: open,
    pageSize: teamFeedPageSize,
  });
  const loadMoreTeamFeed = useCallback(() => {
    isLoadingMoreRef.current = true;
    setTeamFeedPageSize((prev) => prev + 10);
  }, []);

  // Help thread - query Firestore for help + relay-response tagged messages
  const [helpThread, setHelpThread] = useState<ManagementTeamMessage[]>([]);
  useEffect(() => {
    if (!open) return;
    const ref = collection(db, COLLECTION);
    const q = query(
      ref,
      where("workspace_id", "==", workspaceId),
      where("tags", "array-contains-any", ["relay-help", "relay-response"]),
      orderBy("sequence", "desc"),
      fbLimit(5)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const msgs: ManagementTeamMessage[] = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          author: data.author || "unknown",
          content: data.content || "",
          tags: Array.isArray(data.tags) ? data.tags : [],
          timestamp: data.timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
          sequence: data.sequence || 0,
          workspace_id: data.workspace_id,
          linkedIssue: data.linkedIssue,
          replyToSequence: data.replyToSequence,
        };
      });
      setHelpThread(msgs); // Newest first — unified feed .reverse() handles display order
    });
    return unsubscribe;
  }, [open, workspaceId]);

  // Persistence
  useEffect(() => {
    if (pos) localStorage.setItem("relay.fc.pos", JSON.stringify(pos));
  }, [pos]);
  useEffect(() => {
    localStorage.setItem("relay.fc.size", JSON.stringify(size));
  }, [size]);
  useEffect(() => {
    localStorage.setItem("relay.fc.helpFilter", String(helpFilterActive));
  }, [helpFilterActive]);
  useEffect(() => {
    localStorage.setItem("relay.fc.favorites", JSON.stringify(favorites));
  }, [favorites]);
  useEffect(() => {
    localStorage.setItem("relay.fc.activeFilters", JSON.stringify(activeFilters));
  }, [activeFilters]);
  useEffect(() => {
    if (tabY != null) localStorage.setItem("relay.fc.tabY", String(tabY));
  }, [tabY]);

  // Auto-scroll on open or new message — skipped once after "Load earlier messages" so
  // pulling in history doesn't yank the view back down to the newest message.
  useEffect(() => {
    if (!open) return;
    if (isLoadingMoreRef.current) {
      isLoadingMoreRef.current = false;
      return;
    }
    requestAnimationFrame(() => {
      if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    });
  }, [open, helpFilterActive, helpThread.length, teamFeed.length]);

  // Notify host on open
  useEffect(() => {
    if (open && typeof window !== "undefined" && window.parent) {
      try {
        window.parent.postMessage(
          { type: "relay-modal-fired", kind: "floating-chat", title: "Relay Chat / Help" },
          "*"
        );
      } catch {}
    }
  }, [open]);

  // Default position calculator
  const defaultPos = useCallback(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return {
      x: Math.max(16, vw - size.w - 32),
      y: Math.max(16, Math.round(vh * 0.13)),
    };
  }, [size.w]);

  // A saved or dragged position can never leave the title bar off-screen: correct it on
  // load (the persistence effect saves it back) and whenever the browser window resizes.
  useEffect(() => {
    const reclamp = () => setPos((cur) => (cur ? clampWindow({ ...cur, width: size.w, height: size.h }) : cur));
    reclamp();
    window.addEventListener("resize", reclamp);
    return () => window.removeEventListener("resize", reclamp);
  }, [size.w, size.h]);

  // Double-click the title bar (not a control inside it) to return to the default size and position.
  const recenter = useCallback((e: React.MouseEvent) => {
    if (!isTitleBarTarget(e.target)) return;
    const c = centerWindow({ width: DEFAULT_SIZE.w, height: DEFAULT_SIZE.h });
    setSize(DEFAULT_SIZE);
    setPos({ x: c.x, y: c.y });
  }, []);

  useEscapeToClose(open, () => setOpen(false));

  // Panel drag
  const startDrag = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (!pos) setPos(defaultPos());
      const startX = e.clientX;
      const startY = e.clientY;
      const p = pos || defaultPos();
      const ox = p.x;
      const oy = p.y;
      const onMove = (ev: MouseEvent) => {
        const { x: nx, y: ny } = clampWindow({ x: ox + (ev.clientX - startX), y: oy + (ev.clientY - startY), width: size.w, height: size.h });
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [pos, defaultPos, size.w, size.h]
  );

  // Panel resize — direction: n/s/e/w/ne/nw/se/sw
  const startResize = useCallback(
    (e: React.MouseEvent, dir: string) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startY = e.clientY;
      const sw = size.w;
      const sh = size.h;
      const p = pos || defaultPos();
      const ox = p.x;
      const oy = p.y;
      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        let nw = sw;
        let nh = sh;
        let nx = ox;
        let ny = oy;
        if (dir.includes('e')) nw = Math.max(MIN_SIZE.w, Math.min(MAX_SIZE.w, sw + dx));
        if (dir.includes('w')) { nw = Math.max(MIN_SIZE.w, Math.min(MAX_SIZE.w, sw - dx)); nx = ox + (sw - nw); }
        if (dir.includes('s')) nh = Math.max(MIN_SIZE.h, Math.min(MAX_SIZE.h, sh + dy));
        if (dir.includes('n')) { nh = Math.max(MIN_SIZE.h, Math.min(MAX_SIZE.h, sh - dy)); ny = oy + (sh - nh); }
        setSize({ w: nw, h: nh });
        setPos({ x: nx, y: ny });
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [size, pos, defaultPos]
  );

  // Collapsed tab drag (vertical only)
  const startTabDrag = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const tabEl = tabRef.current;
    if (!tabEl) return;
    const rect = tabEl.getBoundingClientRect();
    const offsetY = e.clientY - rect.top;
    const tabH = rect.height;
    let moved = false;
    const onMove = (ev: MouseEvent) => {
      const dy = Math.abs(ev.clientY - e.clientY);
      if (dy > 4) moved = true;
      const ny = Math.min(Math.max(8, ev.clientY - offsetY), window.innerHeight - tabH - 8);
      setTabY(ny);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      if (!moved) handleOpen();
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    e.preventDefault();
  }, []);

  const handleOpen = useCallback(() => {
    if (!pos) setPos(defaultPos());
    setOpen(true);
  }, [pos, defaultPos]);

  // Favorites management
  const addFavorite = useCallback((name: string) => {
    setFavorites((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setPickerOpen(false);
  }, []);

  const removeFavorite = useCallback((name: string) => {
    setFavorites((prev) => prev.filter((n) => n !== name));
    setActiveFilters((prev) => prev.filter((n) => n !== name));
  }, []);

  const toggleFilter = useCallback((name: string) => {
    setActiveFilters((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  }, []);

  const clearFilters = useCallback(() => setActiveFilters([]), []);

  // Filtered feed — help filter shows relay-help/relay-response thread; person filters show author subset
  const filteredFeed = helpFilterActive
    ? helpThread
    : favorites.length === 0
      ? []
      : activeFilters.length === 0
        ? teamFeed
        : teamFeed.filter((m) => activeFilters.includes(m.author));

  const filterMode = helpFilterActive
    ? "help"
    : activeFilters.length === 0 ? null : activeFilters.length === 1 ? "individual" : "group";

  // Quick-post to @relay (used by Help drawer Quick Topics)
  const handleHelpPost = useCallback(async (topic: string) => {
    const content = topic.includes("@relay") ? topic : `@relay ${topic}`;
    const tags = ["relay-help", "chat"];
    try {
      // seq #22352 — was its own inline setDoc(increment(1)) + separate
      // getDocFromServer() read-back, the exact non-atomic pattern that let
      // two concurrent posts land on the same sequence number (see
      // postWithSequence's fix comment). Delegates to the shared,
      // transaction-based helper instead of reimplementing it.
      await postWithSequence(db, {
        author: viewerAgentId,
        content,
        tags,
        workspace_id: workspaceId,
        origin: "relay-composer",
      });
    } catch (err) {
      console.error("[FloatingChat Help] quick post failed:", err);
    }
  }, [viewerAgentId, workspaceId]);

  const p = pos || defaultPos();

  return (
    <>
      {/* Collapsed tab */}
      {!open && (
        <button
          ref={tabRef}
          onMouseDown={startTabDrag}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateX(0)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateX(4px)";
          }}
          aria-label="Open team chat (drag to reposition)"
          title="Click to open — drag to reposition"
          style={{
            position: "fixed",
            right: 0,
            top: tabY != null ? `${tabY}px` : "55%",
            transform: "translateX(4px)",
            transition: "transform 140ms ease-out",
            background: MC.tealLight,
            border: `1.5px solid ${MC.teal}`,
            borderRight: "none",
            borderTopLeftRadius: 14,
            borderBottomLeftRadius: 14,
            padding: "14px 6px 16px 7px",
            cursor: "move",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            boxShadow: "-2px 4px 16px rgba(5,71,80,0.2)",
            zIndex: 180,
            minWidth: 42,
          }}
        >
          {/* Grip dots */}
          <span
            aria-hidden
            style={{
              display: "flex",
              gap: 3,
              marginBottom: 2,
            }}
          >
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: MC.tealDark,
                opacity: 0.6,
              }}
            />
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: MC.tealDark,
                opacity: 0.6,
              }}
            />
            <span
              style={{
                width: 4,
                height: 4,
                borderRadius: "50%",
                background: MC.tealDark,
                opacity: 0.6,
              }}
            />
          </span>

          {/* Relay logo with starburst */}
          <svg width="30" height="30" viewBox="0 0 1024 1024" fill="none">
            {/* Center starburst glow */}
            <circle cx="512" cy="485" r="45" fill={MC.yellow} opacity="0.85" />
            <circle cx="512" cy="485" r="32" fill="#FFF9E6" />
            <path d="M506.954 387.143C505.194 386.585 503.478 385.898 501.82 385.087C495.998 382.287 491.076 377.09 489.084 370.887C487.096 364.776 487.723 358.116 490.815 352.483C493.912 346.834 499.198 342.708 505.43 341.074C534.305 333.489 548.849 379.645 514.048 386.774C514.136 400.458 514.468 417.714 513.908 431.234C514.551 432.918 517.883 432.563 520.076 432.964C530.926 434.947 538.411 439.411 546.457 446.598C554.188 439.4 564.638 427.798 572.388 419.855C573.898 421.526 575.639 422.973 577.557 424.152C570.54 431.392 556.714 444.755 550.677 451.983C557.09 461.586 559.882 467.274 560.828 478.954L604.609 478.951C604.531 480.932 604.394 483.122 604.444 485.088L576.945 484.857C573.557 484.834 563.761 484.666 560.88 485.247C559.095 498.454 556.547 503.768 548.621 514.346C558.182 524.614 568.225 534.377 577.757 544.671C575.945 545.795 574.294 547.16 572.848 548.727C566.788 542.628 560.794 536.463 554.867 530.234C551.778 526.999 547.176 521.883 543.909 519.071C534.055 525.986 526.747 529.947 514.486 530.655L514.544 582.862C512.09 582.689 510.741 583.057 507.894 582.653C507.698 565.317 507.693 547.979 507.879 530.642C494.78 529.472 488.119 525.595 477.888 517.952C468.293 528.053 458.6 538.059 448.81 547.97C447.207 546.361 445.38 544.993 443.385 543.906C453.226 533.72 463.17 523.633 473.215 513.649C466.053 503.818 463.508 497.65 462.228 485.489C446.467 485 428.949 485.358 413.122 485.384C413.428 483.283 413.441 481.149 413.163 479.044L462.285 478.946C462.999 468.535 466.153 460.966 471.847 452.457C465.345 444.605 452.879 432.743 445.396 424.612C447.206 423.43 448.847 422.006 450.272 420.38C458.334 429.193 467.779 439.085 476.5 447.291C485.065 438.859 494.821 433.084 507.1 432.379C506.706 427.748 506.903 420.659 506.908 415.821L506.954 387.143Z" fill={MC.tealLight} />
            <path d="M445.396 424.612C434.937 431.443 420.975 429.007 413.447 419.037C405.92 409.067 407.4 394.971 416.834 386.783C426.268 378.594 440.432 379.111 449.244 387.965C458.056 396.819 458.505 410.986 450.272 420.38C448.847 422.006 447.206 423.43 445.396 424.612Z" fill={MC.tealDark} />
            <path d="M572.388 419.855C564.049 410.629 564.258 396.527 572.868 387.553C581.477 378.579 595.559 377.785 605.122 385.735C614.686 393.685 616.478 407.675 609.228 417.779C601.978 427.883 588.151 430.666 577.557 424.152C575.639 422.973 573.898 421.526 572.388 419.855Z" fill={MC.orange} />
            <path d="M577.757 544.671C588.319 538.118 602.152 540.868 609.403 550.963C616.655 561.057 614.845 575.045 605.263 582.961C595.681 590.878 581.604 590.017 573.058 580.992C564.512 571.966 564.42 557.863 572.848 548.727C574.294 547.16 575.945 545.795 577.757 544.671Z" fill={MC.tealLight} />
            <path d="M448.81 547.97C457.544 556.732 458.027 570.754 449.916 580.096C441.805 589.438 427.855 590.929 417.953 583.512C408.05 576.094 405.559 562.288 412.244 551.878C418.929 541.467 432.521 537.988 443.385 543.906C445.38 544.993 447.207 546.361 448.81 547.97Z" fill={MC.orange} />
          </svg>

          {/* relay label (vertical) */}
          <span
            aria-hidden
            style={{
              writingMode: "vertical-rl",
              fontFamily: "var(--font-ui)",
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: "-0.015em",
              color: MC.tealDark,
            }}
          >
            relay
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Relay chat and help"
          style={{
            position: "fixed",
            left: p.x,
            top: p.y,
            width: size.w,
            height: size.h,
            background: MC.paper,
            border: `2px solid ${MC.teal}`,
            borderRadius: 18,
            boxShadow: `0 24px 60px rgba(5,71,80,0.22), 0 4px 12px rgba(20,20,18,0.08)`,
            display: "flex",
            flexDirection: "column",
            zIndex: 200,
            overflow: "hidden",
            fontFamily: "var(--font-ui)",
          }}
        >
          {/* Misregistration ghosts */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -2,
              border: `2px solid ${MC.orange}`,
              borderRadius: 18,
              transform: "translate(4px, 5px)",
              opacity: 0.55,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: -2,
              border: `2px solid ${MC.yellow}`,
              borderRadius: 18,
              transform: "translate(-3px, 2px)",
              opacity: 0.45,
              zIndex: -1,
              pointerEvents: "none",
            }}
          />

          {/* Header rail */}
          <div
            onMouseDown={startDrag}
            onDoubleClick={recenter}
            style={{
              background: MC.tealLight,
              borderBottom: `1px solid ${MC.teal}`,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: "10px 12px",
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "move",
              userSelect: "none",
            }}
          >
            {/* Relay starburst mark */}
            <img src="/relay-mark.svg" style={{ width: 25, height: 25, display: "block" }} alt="" />

            <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: "var(--font-mark)",
                  fontSize: 20,
                  fontWeight: 400,
                  letterSpacing: "-0.015em",
                  color: MC.tealDark,
                  lineHeight: 1.1,
                }}
              >
                relay
              </span>
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: MC.tealDark,
                  opacity: 0.75,
                  lineHeight: 1.2,
                }}
              >
                {helpFilterActive ? "HELP CHANNEL" : "TEAM CHAT"}
              </span>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              title="Close"
              style={{
                background: "transparent",
                border: "none",
                color: MC.tealDark,
                fontSize: 24,
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
                width: 24,
                height: 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>

          {/* Unified chat panel */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Favorites bar — includes Help filter pill */}
            <FavoritesBar
              roster={roster}
              favorites={favorites}
              activeFilters={activeFilters}
              helpFilterActive={helpFilterActive}
              onToggleFilter={toggleFilter}
              onRemoveFavorite={removeFavorite}
              onOpenPicker={() => setPickerOpen(true)}
              onToggleHelp={() => setHelpFilterActive((prev) => !prev)}
            />

            {/* Context strip */}
            {filterMode === "help" ? (
              <div
                style={{
                  padding: "8px 12px",
                  background: "#EEF8F7",
                  borderBottom: `1px solid ${MC.teal}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: MC.teal, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: MC.tealDark, fontWeight: 500 }}>
                  Help channel · <b>relay-help</b> + <b>relay-response</b> messages
                </span>
                <button
                  type="button"
                  onClick={() => setHelpFilterActive(false)}
                  style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MC.tealDark, background: "transparent", border: "none", cursor: "pointer", padding: "2px 6px" }}
                >
                  Clear
                </button>
              </div>
            ) : filterMode ? (
              <div
                style={{
                  padding: "8px 12px",
                  background: "#F4EFDD",
                  borderBottom: `1px solid ${MC.line}`,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: MC.orange, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: MC.inkSoft, fontWeight: 500 }}>
                  {filterMode === "individual" ? (
                    <>Viewing <b style={{ color: MC.tealDark }}>{activeFilters[0]}</b> only · 1:1 thread</>
                  ) : (
                    <>Group filter · {activeFilters.length} selected · showing any of <b style={{ color: MC.tealDark }}>{activeFilters.join(", ")}</b></>
                  )}
                </span>
                <button
                  type="button"
                  onClick={clearFilters}
                  style={{ marginLeft: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: MC.tealDark, background: "transparent", border: "none", cursor: "pointer", padding: "2px 6px" }}
                >
                  Clear
                </button>
              </div>
            ) : null}

            {/* Roster picker */}
            {pickerOpen && (
              <RosterPicker
                roster={roster}
                favorites={favorites}
                onAdd={addFavorite}
                onClose={() => setPickerOpen(false)}
              />
            )}

            {/* Help drawer — slides in when Help filter is active */}
            {helpFilterActive && <HelpDrawer onQuickPost={handleHelpPost} />}

            {/* Unified feed */}
            <div
              ref={chatScrollRef}
              style={{ flex: 1, overflowY: "auto", padding: "8px 0", background: MC.paper }}
            >
              {loading && !helpFilterActive ? (
                <div style={{ padding: 20, textAlign: "center", color: MC.inkMuted, fontSize: 13 }}>Loading...</div>
              ) : filteredFeed.length === 0 ? (
                helpFilterActive ? (
                  <div style={{ padding: "40px 20px", textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--font-ui)", fontSize: 18, fontWeight: 600, color: MC.tealDark, marginBottom: 10 }}>
                      What do you need help with?
                    </div>
                    <div style={{ fontSize: 12, color: MC.inkMuted, marginBottom: 16 }}>
                      Ask @relay about MCP setup, Charter rules, pre-flight protocol, or anything else.
                    </div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: MC.inkMuted, opacity: 0.7 }}>
                      ↑ Try a topic above
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 20, textAlign: "center", color: MC.inkMuted, fontSize: 13 }}>No messages</div>
                )
              ) : (
                <>
                  {!helpFilterActive && teamFeedHasMore && (
                    <div style={{ textAlign: "center", padding: "2px 0 10px" }}>
                      <button
                        type="button"
                        onClick={loadMoreTeamFeed}
                        style={{
                          background: MC.paper,
                          border: `1.5px solid ${MC.teal}`,
                          borderRadius: 999,
                          padding: "5px 12px",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.06em",
                          color: MC.tealDark,
                          cursor: "pointer",
                          fontFamily: "var(--font-ui)",
                        }}
                      >
                        Load earlier messages
                      </button>
                    </div>
                  )}
                  {[...filteredFeed].reverse().map((msg) => (
                    <Bubble key={msg.id} message={msg} viewerAgentId={viewerAgentId} />
                  ))}
                </>
              )}
            </div>

            {/* Composer */}
            <div style={{ borderTop: `1px solid ${MC.line}`, background: "#FBFAF4", padding: "8px 10px" }}>
              <ERComposerV1
                user={user}
                participants={participants}
                viewMode="log"
                onOptimisticInsert={onOptimisticInsert}
                onOptimisticRollback={onOptimisticRollback}
                workspaceId={workspaceId}
                projects={projects}
                activeProjectSlug={activeProjectSlug}
                isHumanLead={isHumanLead}
              />
            </div>
          </div>

          {/* Resize handles — all 8 directions. Only "se" (bottom-right) gets a
              visible affordance — ui/standardize-resize-handle — matching every
              other custom-resizable surface in the app. The other 3 corners stay
              invisible hit-zones (cursor-only feedback), same as before. */}
          {/* Corners */}
          {(["nw", "ne", "sw"] as const).map((dir) => {
            const cursors: Record<string, string> = { nw: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize" };
            const pos2: Record<string, React.CSSProperties> = {
              nw: { top: 0, left: 0 },
              ne: { top: 0, right: 0 },
              sw: { bottom: 0, left: 0 },
            };
            return (
              <div
                key={dir}
                role="button"
                aria-label={`Resize ${dir}`}
                onMouseDown={(e) => startResize(e, dir)}
                style={{
                  position: "absolute",
                  width: 14,
                  height: 14,
                  cursor: cursors[dir],
                  zIndex: 10,
                  ...pos2[dir],
                }}
              />
            );
          })}
          <ResizeHandle onMouseDown={(e) => startResize(e, "se")} style={{ zIndex: 10 }} />
          {/* Edges */}
          {(["n", "s", "e", "w"] as const).map((dir) => {
            const cursors: Record<string, string> = { n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize" };
            const edgeStyle: Record<string, React.CSSProperties> = {
              n: { top: 0, left: 14, right: 14, height: 5 },
              s: { bottom: 0, left: 14, right: 14, height: 5 },
              e: { right: 0, top: 14, bottom: 14, width: 5 },
              w: { left: 0, top: 14, bottom: 14, width: 5 },
            };
            return (
              <div
                key={dir}
                role="button"
                aria-label={`Resize ${dir}`}
                onMouseDown={(e) => startResize(e, dir)}
                style={{
                  position: "absolute",
                  cursor: cursors[dir],
                  zIndex: 9,
                  ...edgeStyle[dir],
                }}
              />
            );
          })}
        </div>
      )}
    </>
  );
});

/* ========== Favorites Bar ========== */
function FavoritesBar({
  roster,
  favorites,
  activeFilters,
  helpFilterActive,
  onToggleFilter,
  onRemoveFavorite,
  onOpenPicker,
  onToggleHelp,
}: {
  roster: RosterEntry[];
  favorites: string[];
  activeFilters: string[];
  helpFilterActive: boolean;
  onToggleFilter: (name: string) => void;
  onRemoveFavorite: (name: string) => void;
  onOpenPicker: () => void;
  onToggleHelp: () => void;
}) {
  return (
    <div
      style={{
        padding: "8px 12px",
        background: "#FBFAF4",
        borderBottom: `1px solid ${MC.line}`,
      }}
    >
      <div
        style={{
          fontSize: 8.5,
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: MC.inkMuted,
          marginBottom: 6,
        }}
      >
        Favorites
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
        {favorites.map((name) => {
          const active = activeFilters.includes(name);
          const dot = AUTHOR_DOT[name] || MC.tealDark;
          const kind = roster.find((r) => r.name === name)?.kind || "ai";
          return (
            <FavPill
              key={name}
              name={name}
              dot={dot}
              kind={kind}
              active={active}
              onToggle={() => onToggleFilter(name)}
              onRemove={() => onRemoveFavorite(name)}
            />
          );
        })}
        <button
          type="button"
          onClick={onOpenPicker}
          style={{
            width: 28,
            height: 28,
            borderRadius: 999,
            background: MC.orange,
            border: `1.5px solid ${MC.tealDark}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          title="Add favorite"
          aria-label="Add favorite"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M6 2 V10 M2 6 H10"
              stroke={MC.tealDark}
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {/* Help filter pill — fixed at end of favorites row */}
        <button
          type="button"
          onClick={onToggleHelp}
          title={helpFilterActive ? "Exit help channel" : "View @relay help conversation"}
          aria-pressed={helpFilterActive}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            background: helpFilterActive ? MC.tealDark : MC.paper,
            border: `1.5px solid ${MC.tealDark}`,
            borderRadius: 999,
            padding: "4px 10px",
            cursor: "pointer",
            fontFamily: "var(--font-ui)",
            fontSize: 11.5,
            fontWeight: helpFilterActive ? 700 : 600,
            color: helpFilterActive ? MC.paper : MC.ink,
            marginLeft: "auto",
          }}
        >
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden>
            <circle cx="5.5" cy="5.5" r="4.5" stroke={helpFilterActive ? MC.paper : MC.tealDark} strokeWidth="1.3" fill="none" />
            <text x="5.5" y="8.5" textAnchor="middle" fontSize="6.5" fontWeight="700" fill={helpFilterActive ? MC.paper : MC.tealDark} fontFamily="var(--font-ui)">?</text>
          </svg>
          help
        </button>
      </div>
    </div>
  );
}

/* ========== Help Drawer ========== */
function HelpDrawer({ onQuickPost }: { onQuickPost: (topic: string) => void }) {
  return (
    <div
      style={{
        padding: "8px 12px",
        borderBottom: `1px solid ${MC.line}`,
        background: "#F4EFDD",
      }}
    >
      <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: MC.inkMuted, marginBottom: 6 }}>
        Slash Commands
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {[
          { command: "/sprint-status", description: "Current sprint summary and track status" },
          { command: "/who-is-working", description: "Which agents are busy and what they're on" },
        ].map(({ command, description }) => (
          <div key={command} style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ background: MC.paper, border: `1.5px solid ${MC.teal}`, borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 700, color: MC.tealDark, fontFamily: "monospace", flexShrink: 0 }}>
              {command}
            </span>
            <span style={{ fontSize: 11, color: MC.inkMuted }}>{description}</span>
          </div>
        ))}
      </div>

      <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: MC.inkMuted, marginTop: 10, marginBottom: 6 }}>
        Quick Topics
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {["MCP setup", "Charter rules", "Pre-flight protocol", "Sprint planning"].map((topic) => (
          <button
            key={topic}
            type="button"
            onClick={() => onQuickPost(topic)}
            style={{ background: MC.paper, border: `1.5px solid ${MC.teal}`, borderRadius: 999, padding: "5px 10px", fontSize: 11.5, fontWeight: 600, color: MC.tealDark, cursor: "pointer", fontFamily: "var(--font-ui)" }}
          >
            {topic}
          </button>
        ))}
      </div>
    </div>
  );
}

function FavPill({
  name,
  dot,
  kind,
  active,
  onToggle,
  onRemove,
}: {
  name: string;
  dot: string;
  kind: "ai" | "human";
  active: boolean;
  onToggle: () => void;
  onRemove: () => void;
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 2,
        background: active ? MC.tealLight : MC.paper,
        border: `1.5px solid ${name === "relay" ? MC.orange : active ? MC.tealDark : MC.teal}`,
        borderRadius: 999,
        padding: "4px 6px 4px 8px",
        boxShadow: active ? `0 1px 0 ${MC.tealDark}` : "none",
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        title={active ? `Stop filtering by ${name}` : `Filter chat to ${name}`}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: 0,
        }}
      >
        {name === "relay" ? (
          <img src="/relay-mark.svg" style={{ width: 21.875, height: 21.875, display: "block", flexShrink: 0 }} alt="" />
        ) : (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: dot,
              border: `1px solid ${MC.tealDark}`,
              flexShrink: 0,
            }}
          />
        )}
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 11.5,
            fontWeight: active ? 700 : 600,
            color: active ? MC.tealDark : MC.ink,
            letterSpacing: "-0.005em",
          }}
        >
          {name}
        </span>
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 8.5,
            fontWeight: 700,
            letterSpacing: "0.14em",
            color: kind === "ai" ? MC.tealDark : MC.orange,
            opacity: 0.75,
            marginLeft: 1,
          }}
        >
          {kind === "ai" ? "AI" : "HUMAN"}
        </span>
      </button>
      <button
        type="button"
        onClick={onRemove}
        title={`Remove ${name} from favorites`}
        aria-label={`Remove ${name}`}
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 0,
          width: 14,
          height: 14,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path
            d="M2 2 L6 6 M6 2 L2 6"
            stroke={MC.tealDark}
            strokeWidth="1.4"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </span>
  );
}

/* ========== Roster Picker ========== */
function RosterPicker({
  roster,
  favorites,
  onAdd,
  onClose,
}: {
  roster: RosterEntry[];
  favorites: string[];
  onAdd: (name: string) => void;
  onClose: () => void;
}) {
  const humans = roster.filter((r) => r.kind === "human");
  const ais = roster.filter((r) => r.kind === "ai");

  return (
    <div
      style={{
        position: "absolute",
        top: 130,
        left: 12,
        right: 12,
        background: MC.paper,
        border: `2px solid ${MC.teal}`,
        borderRadius: 12,
        boxShadow: "0 8px 24px rgba(5,71,80,0.2)",
        zIndex: 10,
        maxHeight: 360,
        overflowY: "auto",
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div
        style={{
          background: MC.tealLight,
          padding: "8px 12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          borderBottom: `1px solid ${MC.teal}`,
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 14,
            fontWeight: 600,
            color: MC.tealDark,
          }}
        >
          Add to favorites
        </span>
        <button
          type="button"
          onClick={onClose}
          title="Close"
          aria-label="Close picker"
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: 0,
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2 2 L8 8 M8 2 L2 8"
              stroke={MC.tealDark}
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* AI group */}
      <PickerGroup label="AI PARTNERS" items={ais} favorites={favorites} onAdd={onAdd} />

      {/* Human group */}
      <PickerGroup label="HUMANS" items={humans} favorites={favorites} onAdd={onAdd} />
    </div>
  );
}

function PickerGroup({
  label,
  items,
  favorites,
  onAdd,
}: {
  label: string;
  items: RosterEntry[];
  favorites: string[];
  onAdd: (name: string) => void;
}) {
  return (
    <div style={{ padding: "6px 6px 8px" }}>
      <div
        style={{
          fontSize: 8.5,
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          color: MC.inkMuted,
          padding: "6px 6px 4px",
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {items.map((p) => {
          const already = favorites.includes(p.name);
          const dot = AUTHOR_DOT[p.name] || MC.tealDark;
          return (
            <button
              key={p.name}
              type="button"
              onClick={() => !already && onAdd(p.name)}
              disabled={already}
              onMouseEnter={(e) => {
                if (!already) e.currentTarget.style.background = "#F4EFDD";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px",
                background: "transparent",
                border: "none",
                borderRadius: 8,
                cursor: already ? "default" : "pointer",
                opacity: already ? 0.55 : 1,
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  background: dot,
                  border: `1px solid ${MC.tealDark}`,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: MC.ink,
                  flex: 1,
                }}
              >
                {p.name}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 10.5,
                  fontWeight: 400,
                  color: MC.inkMuted,
                }}
              >
                {p.role}
              </span>
              <span
                style={{
                  width: 28,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {already ? (
                  <span
                    style={{
                      color: MC.inkMuted,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                    }}
                  >
                    ADDED
                  </span>
                ) : (
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background: MC.orange,
                      border: `1px solid ${MC.tealDark}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M6 2 V10 M2 6 H10"
                        stroke={MC.tealDark}
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ========== Bubble Component ========== */
export function Bubble({
  message,
  viewerAgentId,
}: {
  message: ManagementTeamMessage;
  viewerAgentId: string;
}) {
  const mine = message.author === viewerAgentId;
  const stroke = mine ? MC.orange : MC.teal;
  const dotColor = AUTHOR_DOT[message.author] || MC.tealDark;
  const isMultiline = message.content.includes("\n");

  // Format timestamp
  const timestamp = new Date(message.timestamp).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <div
      style={{
        display: "flex",
        justifyContent: mine ? "flex-end" : "flex-start",
        padding: "5px 10px",
      }}
    >
      <div style={{ maxWidth: "85%", position: "relative" }}>
        <div
          style={{
            background: MC.paper,
            border: `1.5px solid ${stroke}`,
            borderRadius: 10,
            overflow: "hidden",
            padding: isMultiline ? "11px 16px 12px" : "11px 18px 12px",
            color: MC.ink,
            fontSize: 13,
            lineHeight: 1.5,
            position: "relative",
            zIndex: 1,
            fontFamily: "var(--font-ui)",
            whiteSpace: "pre-wrap",
          }}
        >
          {/* Author header */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
            {message.author === "relay" ? (
              <img src="/relay-mark.svg" style={{ width: 18.75, height: 18.75, display: "block", flexShrink: 0 }} alt="" />
            ) : (
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: dotColor,
                  border: `1px solid ${MC.tealDark}`,
                  flexShrink: 0,
                }}
              />
            )}
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "-0.005em",
                color: MC.ink,
              }}
            >
              {message.author}
            </span>
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: MC.ink,
                opacity: 0.7,
              }}
            >
              {timestamp.toUpperCase()}
            </span>
            {message.sequence > 0 && (
              <span
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  color: MC.inkMuted,
                  opacity: 0.65,
                  marginLeft: "auto",
                  flexShrink: 0,
                }}
              >
                #{message.sequence}
              </span>
            )}
          </div>

          {/* Content */}
          <div style={{ wordBreak: "break-word", color: MC.ink, fontWeight: 400, padding: "10px 12px" }}>
            <HowRelayWorksText text={message.content} />
          </div>

          {/* Tags */}
          {message.tags && message.tags.length > 0 && (
            <div style={{ marginTop: 7, display: "flex", flexWrap: "wrap", gap: 4 }}>
              {message.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  style={{
                    display: "inline-block",
                    fontFamily: "var(--font-ui)",
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    padding: "3px 9px",
                    background: MC.paper,
                    color: MC.ink,
                    border: `1.25px solid ${stroke}`,
                    borderRadius: 999,
                    lineHeight: "14px",
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Bubble tail */}
        <BubbleTail mine={mine} fill={MC.paper} stroke={stroke} />
      </div>
    </div>
  );
}

function BubbleTail({ mine, fill, stroke }: { mine: boolean; fill: string; stroke: string }) {
  const w = 14;
  const h = 11;
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      style={{
        position: "absolute",
        top: `calc(100% - 1.5px)`,
        [mine ? "right" : "left"]: 20,
        overflow: "visible",
        zIndex: 0,
      }}
    >
      <path d={mine ? `M ${w} 0 L 0 0 L ${w - 2} ${h - 1} Z` : `M 0 0 L ${w} 0 L 2 ${h - 1} Z`} fill={fill} />
      <path
        d={mine ? `M ${w} 0 L ${w - 2} ${h - 1} L 0 0` : `M 0 0 L 2 ${h - 1} L ${w} 0`}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
